import { List as IList, Map as IMap } from 'immutable';
import { createSelector } from 'reselect';
import { CARD_TYPES } from '../constants/constants';
import alphaNumericNormalizer from '../addressFields/validate/alphaNumericNormalizer';

export const ordinalSort = (itemA, itemB) => (itemA.get('ordinal') > itemB.get('ordinal') ? 1 : -1);

export const getFaceplatesInfo = state => state.get('faceplateInfo', IMap());

export const getFaceplateGroups = createSelector(
    getFaceplatesInfo,
    faceplateInfo => faceplateInfo.get('faceplateGroups', IList())
);

export const hasFaceplateGroups = createSelector(
    getFaceplateGroups,
    groups => groups.size > 0
);

export const getSelectedFaceplate = createSelector(
    getFaceplatesInfo,
    faceplateInfo => faceplateInfo.get('selectedFaceplate', IMap())
);

export const getSelectedFaceplateCode = createSelector(
    getSelectedFaceplate,
    faceplate => faceplate.get('code')
);

export const getSelectedFaceplateCategory = createSelector(
    getSelectedFaceplate,
    faceplate => faceplate.get('category')
);

export const getSelectedDigitalFaceplateCategory = createSelector(
    getFaceplatesInfo,
    faceplateInfo => faceplateInfo.get('digitalFaceplateCategory', '')
);

export const getSelectedPhysicalFaceplateCategory = createSelector(
    getFaceplatesInfo,
    faceplateInfo => faceplateInfo.get('physicalFaceplateCategory', '')
);

export const getSelectedFaceplateImageURL = createSelector(
    getSelectedFaceplate,
    faceplate => faceplate.get('imageURL', '')
);

/* Fetch all the categories of faceplates */
export const getAllCategories = createSelector(
    getFaceplateGroups,
    faceplateGroups => faceplateGroups.map(data => data.get('name'))
);

export const getDigitalFaceplateGroups = createSelector(
    getFaceplateGroups,
    faceplateGroups => faceplateGroups.filter(group => group.get('delivery') === 'ELECTRONIC').sort(ordinalSort)
);

export const getPhysicalFaceplateGroups = createSelector(
    getFaceplateGroups,
    faceplateGroups => faceplateGroups.filter(group => group.get('delivery') === 'PHYSICAL').sort(ordinalSort)
);

export const getDigitalCategories = createSelector(
    getDigitalFaceplateGroups,
    faceplateGroups => faceplateGroups.map(data => data.get('name', ''))
);

/* Fetch all the physical categories of faceplates */
export const getPhysicalCategories = createSelector(
    getPhysicalFaceplateGroups,
    faceplateGroups => faceplateGroups.map(data => data.get('name'))
);

export const hasDigitalCategories = createSelector(
    getDigitalFaceplateGroups,
    groups => (groups.size >= 1)
);

export const hasPhysicalCategories = createSelector(
    getPhysicalFaceplateGroups,
    groups => (groups.size >= 1)
);

export const primaryFaceplateForGroup = group => (
    group.get('faceplates', IList()).sort(ordinalSort).first(0)
);

export const getPhysicalPrimaryFaceplate = createSelector(
    getPhysicalFaceplateGroups,
    groups => primaryFaceplateForGroup(groups.get(0, IMap()))
);

export const getDigitalPrimaryFaceplate = createSelector(
    getDigitalFaceplateGroups,
    groups => primaryFaceplateForGroup(groups.get(0, IMap()))
);

export const isPhysicalCardSelected = createSelector(
    getFaceplatesInfo,
    faceplateInfo => faceplateInfo.get('selectedCardType') === CARD_TYPES.Physical
);

export const getCurrentCardType = createSelector(
    getFaceplatesInfo,
    faceplateInfo => faceplateInfo.get('selectedCardType') || CARD_TYPES.Digital
);

export const getSelectedCardType = createSelector(
    getFaceplatesInfo,
    faceplateInfo => faceplateInfo.get('selectedCardType')
);

export const getBuyNowDigitalAfterLimit = createSelector(
    getFaceplatesInfo,
    faceplateInfo => faceplateInfo.get('buyNowDigitalAfterCardLimit')
);

export const isFaceplateInPreferredGroup = createSelector(
    getDigitalFaceplateGroups,
    getPhysicalFaceplateGroups,
    (digitalFaceplateGroups, physicalFaceplateGroups) => (cardTypeIsPlastic, faceplateCode) => {
        if (cardTypeIsPlastic !== null) {
            if (cardTypeIsPlastic) {
                return physicalFaceplateGroups.some(group => group.get('faceplates')
                    .some(faceplate => faceplate.get('code') === faceplateCode));
            }
            return digitalFaceplateGroups.some(group => group.get('faceplates')
                .some(faceplate => faceplate.get('code') === faceplateCode));
        }
        return false;
    }
);


export const getFaceplateByCode = createSelector(
    getDigitalFaceplateGroups,
    getPhysicalFaceplateGroups,
    isPhysicalCardSelected,
    (digitalFaceplateGroups, physicalFaceplateGroups, isPlastic) => (faceplateCode) => {
        const groups = isPlastic ? physicalFaceplateGroups : digitalFaceplateGroups;
        let foundFaceplate = null;
        groups.some((group) => {
            const faceplate = group.get('faceplates')
                .find(fp => fp.get('code') === faceplateCode);
            if (faceplate) {
                foundFaceplate = faceplate;
                return true; 
            }
            return false;
        });

        return foundFaceplate;
    }
);

export const getFaceplateCategoryByCode = createSelector(
    getDigitalFaceplateGroups,
    getPhysicalFaceplateGroups,
    isPhysicalCardSelected,
    (digitalFaceplateGroups, physicalFaceplateGroups, isPlastic) => (faceplateCode) => {
        const groups = isPlastic ? physicalFaceplateGroups : digitalFaceplateGroups;
        let foundCategory = null;
        groups.some((group) => {
            const faceplate = group.get('faceplates')
                .find(fp => fp.get('code') === faceplateCode);
            if (faceplate) {
                foundCategory = group.get('name');
                return true; 
            }
            return false;
        });

        return foundCategory;
    }
);

export const getValidPreferredFaceplateCategory = createSelector(
    getDigitalFaceplateGroups,
    getPhysicalFaceplateGroups,
    isPhysicalCardSelected,
    (digitalFaceplateGroups, physicalFaceplateGroups, isPlastic) => (FaceplateCategoryCode) => {
        const groups = isPlastic ? physicalFaceplateGroups : digitalFaceplateGroups;
        let validFaceplateCategoryCode = null;
        groups.some((group) => {
            if (alphaNumericNormalizer(group.get('name')).toLowerCase() === FaceplateCategoryCode) {
                validFaceplateCategoryCode = group.get('name');
                return true; 
            }
            return false;
        });
        return validFaceplateCategoryCode;
    }
);

export const getDefaultFaceplateByFaceplateCategory = createSelector(
    getDigitalFaceplateGroups,
    getPhysicalFaceplateGroups,
    isPhysicalCardSelected,
    (digitalFaceplateGroups, physicalFaceplateGroups, isPlastic) => (FaceplateCategoryCode) => {
        const groups = isPlastic ? physicalFaceplateGroups : digitalFaceplateGroups;
        let firstFaceplate = null;
        groups.some((group) => {
            if (alphaNumericNormalizer(group.get('name')).toLowerCase() === FaceplateCategoryCode) {
                firstFaceplate = group.get('faceplates').first();
                return true; 
            }
            return false;
        });
        return firstFaceplate;
    }
);


export const isClickedOnUYO = createSelector(
    getFaceplatesInfo,
    faceplateInfo => faceplateInfo.get('clickedOnUYO', false)
);
